import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const verifyToken = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/verify');
      if (response.data.success) {
        setUser(response.data.data.user);
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    // Configura o token no axios se existir
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token, verifyToken]);

  useEffect(() => {
    // Interceptor para logout automático em caso de 401
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Só faz logout se for 401 e não for erro de "Token não fornecido" (que pode acontecer na primeira requisição)
        if (error.response?.status === 401 && token && error.response?.data?.error !== 'Token não fornecido') {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token, logout]);

  const login = async (nickname, senha) => {
    try {
      const response = await axios.post('/api/auth/login', { nickname, senha });
      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        
        // Configura o token no axios ANTES de atualizar o estado
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        localStorage.setItem('token', newToken);
        
        // Atualiza o estado após configurar o token
        setToken(newToken);
        setUser(userData);
        
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao fazer login'
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

